import { createContext, type ReactNode, useContext } from "react";

import type { CmsModuleRenderProps, ImportantDisclosureFootnoteProps } from "./renderProps";

const FOOTNOTE_REFERENCE_PATTERN =
  /\[\[([a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?)]]/g;

export interface NumberedDisclosureFootnote
  extends ImportantDisclosureFootnoteProps {
  key: string;
  number: number;
  targetId: string;
}

export interface DisclosureFootnoteRegistry {
  byKey: Map<string, NumberedDisclosureFootnote>;
  items: NumberedDisclosureFootnote[];
}

const EMPTY_REGISTRY: DisclosureFootnoteRegistry = {
  byKey: new Map(),
  items: [],
};

const DisclosureFootnoteContext =
  createContext<DisclosureFootnoteRegistry>(EMPTY_REGISTRY);

export function DisclosureFootnoteProvider({
  registry,
  children,
}: {
  registry: DisclosureFootnoteRegistry;
  children: ReactNode;
}) {
  return (
    <DisclosureFootnoteContext.Provider value={registry}>
      {children}
    </DisclosureFootnoteContext.Provider>
  );
}

export function useDisclosureFootnotes(): DisclosureFootnoteRegistry {
  return useContext(DisclosureFootnoteContext);
}

export function buildDisclosureFootnoteRegistry(
  modules: CmsModuleRenderProps[],
): DisclosureFootnoteRegistry {
  const footnotesByKey = new Map<string, ImportantDisclosureFootnoteProps>();
  for (const module of modules) {
    for (const footnote of module.importantDisclosures?.footnotes ?? []) {
      const key = footnote.key?.trim();
      if (key && footnote.body?.trim() && !footnotesByKey.has(key)) {
        footnotesByKey.set(key, footnote);
      }
    }
  }

  const orderedKeys: string[] = [];
  const seenKeys = new Set<string>();
  for (const module of modules) {
    for (const key of disclosureReferenceKeys(module)) {
      if (footnotesByKey.has(key) && !seenKeys.has(key)) {
        seenKeys.add(key);
        orderedKeys.push(key);
      }
    }
  }
  for (const key of footnotesByKey.keys()) {
    if (!seenKeys.has(key)) {
      orderedKeys.push(key);
    }
  }

  const items = orderedKeys.map((key, index) => ({
    ...footnotesByKey.get(key)!,
    key,
    number: index + 1,
    targetId: disclosureFootnoteTargetId(key),
  }));
  return {
    byKey: new Map(items.map((item) => [item.key, item])),
    items,
  };
}

export function disclosureFootnoteTargetId(key: string): string {
  return `cms-footnote-${key}`;
}

export function splitDisclosureReferenceTokens(text: string) {
  const parts: { text: string; key?: string }[] = [];
  let lastIndex = 0;
  for (const match of text.matchAll(FOOTNOTE_REFERENCE_PATTERN)) {
    const matchIndex = match.index ?? 0;
    const key = match[1];
    if (!key) {
      continue;
    }
    if (matchIndex > lastIndex) {
      parts.push({ text: text.slice(lastIndex, matchIndex) });
    }
    parts.push({ text: match[0], key });
    lastIndex = matchIndex + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push({ text: text.slice(lastIndex) });
  }
  return parts.length ? parts : [{ text }];
}

function disclosureReferenceKeys(module: CmsModuleRenderProps): string[] {
  const keys: string[] = [];
  collectReferences(module.hero?.eyebrow, keys);
  collectReferences(module.hero?.headline, keys);
  collectReferences(module.hero?.body, keys);
  collectReferences(module.advisorProfile?.title, keys);
  collectReferences(module.advisorProfile?.bio, keys);
  collectReferences(module.contactSection?.body, keys);
  collectReferences(module.leadForm?.eyebrow, keys);
  collectReferences(module.leadForm?.heading, keys);
  collectReferences(module.leadForm?.body, keys);
  collectReferences(module.infoCardGrid?.eyebrow, keys);
  collectReferences(module.infoCardGrid?.heading, keys);
  collectReferences(module.infoCardGrid?.body, keys);
  for (const item of module.infoCardGrid?.items ?? []) {
    collectReferences(item.title, keys);
    collectReferences(item.body, keys);
  }
  collectReferences(module.mediaTextSection?.eyebrow, keys);
  collectReferences(module.mediaTextSection?.heading, keys);
  collectReferences(module.mediaTextSection?.body, keys);
  collectReferences(module.processSteps?.eyebrow, keys);
  collectReferences(module.processSteps?.heading, keys);
  collectReferences(module.processSteps?.body, keys);
  for (const step of module.processSteps?.steps ?? []) {
    collectReferences(step.title, keys);
    collectReferences(step.body, keys);
  }
  collectReferences(module.quoteSection?.eyebrow, keys);
  collectReferences(module.quoteSection?.quote, keys);
  collectReferences(module.quoteSection?.attributionTitle, keys);
  collectReferences(module.resourceList?.eyebrow, keys);
  collectReferences(module.resourceList?.heading, keys);
  collectReferences(module.resourceList?.body, keys);
  for (const item of module.resourceList?.items ?? []) {
    collectReferences(item.title, keys);
    collectReferences(item.description, keys);
  }
  collectReferences(module.faq?.eyebrow, keys);
  collectReferences(module.faq?.heading, keys);
  for (const item of module.faq?.items ?? []) {
    collectReferences(item.question, keys);
    collectReferences(item.answer, keys);
  }
  return keys;
}

function collectReferences(value: string | undefined, keys: string[]) {
  if (!value) {
    return;
  }
  for (const match of value.matchAll(FOOTNOTE_REFERENCE_PATTERN)) {
    if (match[1]) {
      keys.push(match[1]);
    }
  }
}
